import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Loader2, ImageIcon, X, Smile } from "lucide-react";
import { Button } from "@tabipla/ui";
import { resizeImage } from "@tabipla/ui/lib/image-resize";
import { apiClient } from "../../../infrastructure/api/client";

// Avatar color options (Tailwind gradient classes)
const ICON_COLORS = [
  "from-cyan-400 to-teal-400",
  "from-blue-400 to-indigo-400",
  "from-purple-400 to-pink-400",
  "from-pink-400 to-rose-400",
  "from-orange-400 to-amber-400",
  "from-emerald-400 to-green-400",
  "from-red-400 to-orange-400",
  "from-violet-400 to-purple-400",
];

// Popular emoji options for group icons
const ICON_EMOJIS = [
  // Travel & Places
  "✈️",
  "🏝️",
  "🗻",
  "🏖️",
  "🌴",
  "🗼",
  "🏰",
  "⛩️",
  // Activities
  "🎯",
  "🎪",
  "🎭",
  "🎨",
  "🎬",
  "🎤",
  "🎸",
  "🎹",
  // Food
  "🍕",
  "🍜",
  "🍣",
  "🍷",
  "☕",
  "🍰",
  "🍔",
  "🌮",
  // Nature
  "🌸",
  "🌻",
  "🌺",
  "🍀",
  "🌈",
  "⭐",
  "🌙",
  "☀️",
  // Animals
  "🐱",
  "🐶",
  "🐰",
  "🦊",
  "🐻",
  "🐼",
  "🦁",
  "🐧",
];

interface GroupIconPickerProps {
  /** Current icon emoji */
  iconEmoji: string | null;
  /** Current icon color (gradient class) */
  iconColor: string | null;
  /** Current icon image URL */
  iconImageUrl: string | null;
  /** Current icon image key (for deletion) - unused but kept for consistency */
  iconImageKey?: string | null;
  /** Callback when emoji/color changes */
  onEmojiChange: (emoji: string | null, color: string | null) => void;
  /** Callback when image is uploaded/removed */
  onImageChange: (url: string | null, key: string | null) => void;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Group name for fallback display */
  groupName?: string;
}

export function GroupIconPicker({
  iconEmoji,
  iconColor,
  iconImageUrl,
  onEmojiChange,
  onImageChange,
  disabled = false,
  groupName = "",
}: GroupIconPickerProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [mode, setMode] = useState<"emoji" | "image">(iconImageUrl ? "image" : "emoji");

  const handleEmojiSelect = (emoji: string) => {
    // When selecting emoji, clear image
    onImageChange(null, null);
    onEmojiChange(emoji, iconColor || ICON_COLORS[0]);
  };

  const handleColorSelect = (color: string) => {
    onEmojiChange(iconEmoji || "✈️", color);
  };

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) {
        return;
      }

      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setUploading(true);

      try {
        const resizedFile = await resizeImage(file);

        const formData = new FormData();
        formData.append("file", resizedFile);
        formData.append("type", "group_icon");

        const response = await apiClient.uploadFormData<{ key: string; url: string }>(
          "/api/upload",
          formData
        );

        if (response.success && response.data) {
          // When uploading image, clear emoji
          onEmojiChange(null, null);
          onImageChange(response.data.url, response.data.key);
        }
      } finally {
        URL.revokeObjectURL(url);
        setPreviewUrl(null);
        setUploading(false);
      }
    },
    [onEmojiChange, onImageChange]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    e.target.value = "";
  };

  const handleRemoveImage = () => {
    onImageChange(null, null);
    // Set default emoji when removing image
    onEmojiChange("✈️", ICON_COLORS[0]);
  };

  const displayUrl = previewUrl || iconImageUrl;

  return (
    <div className="space-y-4">
      <label className="text-sm font-medium">{t("groups.iconSettings", "アイコン設定")}</label>

      {/* Preview */}
      <div className="flex justify-center">
        <div className="relative">
          {displayUrl ? (
            <>
              <img
                src={displayUrl}
                alt="Group icon"
                className={`h-20 w-20 rounded-full object-cover shadow-lg ${previewUrl ? "opacity-50" : ""}`}
              />
              {uploading && (
                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
                  <Loader2 className="h-6 w-6 animate-spin text-white" />
                </div>
              )}
              {!uploading && !disabled && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-white shadow-md transition-transform hover:scale-110"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </>
          ) : (
            <div
              className={`flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br ${iconColor || ICON_COLORS[0]} text-4xl shadow-lg`}
            >
              {iconEmoji || groupName?.charAt(0).toUpperCase() || "✈️"}
            </div>
          )}
        </div>
      </div>

      {/* Mode toggle buttons */}
      <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
        <button
          type="button"
          onClick={() => setMode("emoji")}
          className={`flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-all ${
            mode === "emoji"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Smile className="h-4 w-4" />
          {t("groups.emojiMode", "絵文字")}
        </button>
        <button
          type="button"
          onClick={() => setMode("image")}
          className={`flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-all ${
            mode === "image"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <ImageIcon className="h-4 w-4" />
          {t("groups.imageMode", "画像")}
        </button>
      </div>

      {/* Emoji mode content */}
      {mode === "emoji" && (
        <div className="space-y-4">
          {/* Emoji Selection */}
          <div className="space-y-2">
            <div className="grid grid-cols-8 gap-1.5">
              {ICON_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => handleEmojiSelect(emoji)}
                  disabled={disabled}
                  className={`flex h-9 w-9 items-center justify-center rounded-lg text-lg transition-colors hover:bg-muted active:scale-95 ${
                    iconEmoji === emoji && !iconImageUrl ? "ring-2 ring-primary ring-offset-2" : ""
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          {/* Color Selection */}
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">
              {t("groups.backgroundColor", "背景色")}
            </label>
            <div className="grid grid-cols-4 gap-2">
              {ICON_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleColorSelect(color)}
                  disabled={disabled}
                  className={`h-8 rounded-lg bg-gradient-to-br ${color} transition-transform hover:scale-105 active:scale-95 ${
                    iconColor === color && !iconImageUrl ? "ring-2 ring-primary ring-offset-2" : ""
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Image mode content */}
      {mode === "image" && (
        <div className="space-y-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleInputChange}
            className="hidden"
            disabled={disabled || uploading}
          />
          <div className="flex justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || uploading}
              className="gap-2"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : iconImageUrl ? (
                <Camera className="h-4 w-4" />
              ) : (
                <ImageIcon className="h-4 w-4" />
              )}
              {iconImageUrl
                ? t("groups.changeImage", "画像を変更")
                : t("groups.selectImage", "画像を選択")}
            </Button>
          </div>
          <p className="text-center text-xs text-muted-foreground">
            {t("groups.iconImageHint", "正方形の画像が最適です（自動でトリミングされます）")}
          </p>
        </div>
      )}
    </div>
  );
}
